import { useEffect, useState, useCallback, useMemo } from "react";
import { useRbac } from "../../context";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "../../components/ui/pagination";
import { TrendingUp, X, Search, User } from "lucide-react";
import { getHighlightsTableData } from "../../services/highlights.service";
import { HighlightsStatsSection } from "../../components/highlights/HighlightsStatsSection";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { SearchableSelect, SearchableSelectOption } from "../../components/ui/searchable-select";
import { getAllUsers, getMySubordinates } from "../../services/usersapi";
import { getCurrentMonthDateRangeLocal } from "../../utils/dateRange";

interface TableRowData {
    _id: string;
    username: string;
    approchCount: number;
    walkinCount: number;
    joinCount: number;
    selectionCount: number;
    scheduledForTomorrowCount: number;
    role: string;
    subJobsCount: number;
    attended: number;
}

interface TableTotalCounts {
    Walkin: number;
    Approached: number;
    SubJobs: number;
    Selected: number;
    Joined: number;
    walkinsForTomorrow: number;
    attended: number;
}

const HighlightsPage = () => {
    const { user } = useRbac();
    const monthDefault = useMemo(() => getCurrentMonthDateRangeLocal(), []);
    const [tableData, setTableData] = useState<TableRowData[]>([]);
    const [totalResults, setTotalResults] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [tableTotalCounts, setTableTotalCounts] = useState<TableTotalCounts>({
        Walkin: 0,
        Approached: 0,
        SubJobs: 0,
        Selected: 0,
        Joined: 0,
        walkinsForTomorrow: 0,
        attended: 0,
    });
    const [tableLoading, setTableLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [startDate, setStartDate] = useState<string>(monthDefault.startDate);
    const [endDate, setEndDate] = useState<string>(monthDefault.endDate);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [roleFilter, setRoleFilter] = useState<string>("all");
    const [selectedManager, setSelectedManager] = useState<string>("all");
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [managerOptions, setManagerOptions] = useState<SearchableSelectOption[]>([]);
    const [isLoadingManagers, setIsLoadingManagers] = useState(false);
    const [managerSearchTerm, setManagerSearchTerm] = useState<string>("");
    const [sortBy, setSortBy] = useState<string>("joining");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

    const handleColumnSort = (column: string) => {
        if (sortBy === column) {
            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
            return;
        }
        setSortBy(column);
        setSortOrder("desc");
        setCurrentPage(1);
    };

    const renderSortIcon = (column: string) => {
        if (sortBy !== column) {
            return (
                <span className="inline-flex flex-col items-center text-[8px] leading-none text-gray-400 ml-1.5">
                    <span>▲</span>
                    <span className="mt-[2px]">▼</span>
                </span>
            );
        }
        return sortOrder === "asc"
            ? <span className="text-[9px] leading-none text-gray-900 ml-1.5">▲</span>
            : <span className="text-[9px] leading-none text-gray-900 ml-1.5">▼</span>;
    };

    const fetchTableData = useCallback(async () => {
        setTableLoading(true);
        setError(null);

        try {
            const dateFrom = startDate || undefined;
            const dateTo = endDate || undefined;
            const search = searchTerm.trim() || undefined;
            const role = roleFilter !== "all" ? roleFilter : undefined;
            const managerId = selectedManager !== "all" ? selectedManager : undefined;
            
            // Pass all filters to backend - backend will handle filtering
            const response = await getHighlightsTableData(
                currentPage,
                dateFrom,
                dateTo,
                rowsPerPage,
                search,
                role,
                managerId,
                sortBy,
                sortOrder
            );
            console.log(response, "===");
            if (response?.status === 'success') {
                const mappedData = response.data.map((item: any) => ({
                    _id: item?._id,
                    username: item?.username,
                    approchCount: item?.Approached || 0,
                    walkinCount: item?.Walkin || 0,
                    joinCount: item?.Joined || 0,
                    selectionCount: item?.Selected || 0,
                    scheduledForTomorrowCount: item?.walkinsForTomorrow || 0,
                    subJobsCount: item?.SubJobs || 0,
                    role: item?.role,
                    attended: item?.attended || 0,
                }));

                setTableData(mappedData);
                
                // Set pagination metadata from API response
                if (response.metadata) {
                    setTotalResults(response.metadata.totalResults || 0);
                    setTotalPages(response.metadata.totalPages || 0);
                    setTableTotalCounts({
                        Walkin: response.metadata.totalCounts?.Walkin || 0,
                        Approached: response.metadata.totalCounts?.Approached || 0,
                        SubJobs: response.metadata.totalCounts?.SubJobs || 0,
                        Selected: response.metadata.totalCounts?.Selected || 0,
                        Joined: response.metadata.totalCounts?.Joined || 0,
                        walkinsForTomorrow: response.metadata.totalCounts?.walkinsForTomorrow || 0,
                        attended: response.metadata.totalCounts?.attended || 0,
                    });
                } else {
                    // Fallback if metadata is not available
                    setTotalResults(mappedData.length);
                    setTotalPages(1);
                    setTableTotalCounts({
                        Walkin: 0,
                        Approached: 0,
                        SubJobs: 0,
                        Selected: 0,
                        Joined: 0,
                        walkinsForTomorrow: 0,
                        attended: 0,
                    });
                }
            }
        } catch (err) {
            console.error("Failed to load table data:", err);
            setError("We couldn't load the table data. Please try again in a moment.");
            setTableData([]);
            setTotalResults(0);
            setTotalPages(0);
            setTableTotalCounts({
                Walkin: 0,
                Approached: 0,
                SubJobs: 0,
                Selected: 0,
                Joined: 0,
                walkinsForTomorrow: 0,
                attended: 0,
            });
        } finally {
            setTableLoading(false);
        }
    }, [currentPage, startDate, endDate, searchTerm, roleFilter, selectedManager, rowsPerPage, sortBy, sortOrder]);

    // Initial table load
    useEffect(() => {
        fetchTableData();
    }, []);

    // When date filters change, reset table to page 1 (tiles reload inside HighlightsStatsSection)
    useEffect(() => {
        setCurrentPage(1);
    }, [startDate, endDate]);

    // When page or filters change, fetch table data
    useEffect(() => {
        fetchTableData();
    }, [fetchTableData]);

    // Reset to page 1 when filters change (but not when page changes)
    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [searchTerm, roleFilter, selectedManager, sortBy, sortOrder]);

    // Fetch SHM and FBDE managers for the dropdown
    const fetchManagers = useCallback(async () => {
        setIsLoadingManagers(true);
        try {
            const params: any = {
                role: ["SHM", "FBDE"],
                limit: "20",
                page: "1",
            };

            if (managerSearchTerm.trim()) {
                params.search = managerSearchTerm.trim();
            }

            const response = user?.role === 'ADMIN' ? await getAllUsers(params) : await getMySubordinates();

            if (response?.data?.users || response?.data?.subordinates) {
                const users = user?.role === 'ADMIN' ? response.data.users : response.data.subordinates;
                const options: SearchableSelectOption[] = [
                    { value: "all", label: user?.role === 'ADMIN' ? 'All Managers' : 'All Recruiters' }
                ];

                options.push(...users.map((user: any) => ({
                    value: user.id,
                    label: user.username || user.email,
                    searchText: `${user.username || ''} ${user.email || ''}`.trim()
                })));

                setManagerOptions(options);
            }
        } catch (error) {
            console.error("Failed to fetch managers:", error);
            setManagerOptions([{ value: "all", label: user?.role === 'ADMIN' ? 'All Managers' : 'All Recruiters' }]);
        } finally {
            setIsLoadingManagers(false);
        }
    }, [managerSearchTerm]);

    // Debounce manager search
    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchManagers();
        }, managerSearchTerm.trim() ? 300 : 0);

        return () => clearTimeout(timeout);
    }, [managerSearchTerm, fetchManagers]);

    // Initial fetch of managers
    useEffect(() => {
        fetchManagers();
    }, []);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            // Scroll to table section instead of top of page
            const tableSection = document.getElementById('highlights-table');
            if (tableSection) {
                tableSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    };

    const startIndex = totalResults > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
    const endIndex = Math.min(currentPage * rowsPerPage, totalResults);

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="mx-auto w-full max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
                {/* Error Message */}
                {error && (
                    <div className="mb-4 rounded-lg border-2 border-red-200 bg-red-50 p-4 shadow-sm">
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0">
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500 shadow-md">
                                    <span className="text-sm font-bold text-white">!</span>
                                </div>
                            </div>
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-red-900">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                <HighlightsStatsSection
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={setStartDate}
                    onEndDateChange={setEndDate}
                    cardTitle="Your Highlights"
                    showAggregateHints={user?.role === "ADMIN"}
                    showTrendChart={user?.role === "ADMIN"}
                />

                {/* Bottom Section - Table with Pagination */}
                {user?.role !== 'HR' && (
                    <section id="highlights-table">
                        <Card className="border border-gray-200 bg-white shadow-sm">
                            <CardHeader className="border-b border-gray-200 bg-gray-50/80 pb-4">
                                <CardTitle className="text-lg font-bold text-gray-900 mb-4">
                                    Highlights Details
                                </CardTitle>
                                {/* Filters Section */}
                                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                                    {/* Search Filter */}
                                    <div className="flex-1 relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <Input
                                            placeholder="Search by username..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="pl-10 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 bg-white"
                                        />
                                    </div>
                                    {/* Role Filter */}
                                    <Select
                                        value={roleFilter}
                                        onValueChange={(value) => {
                                            setRoleFilter(value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <SelectTrigger className="w-full sm:w-[180px] h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 bg-white">
                                            <SelectValue placeholder="All Roles" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Roles</SelectItem>
                                            <SelectItem value="HR">HR</SelectItem>
                                            <SelectItem value="HM">HM</SelectItem>
                                            <SelectItem value="SHM">SHM</SelectItem>
                                            <SelectItem value="FBDE">FBDE</SelectItem>
                                            <SelectItem value="CONSULTANT">CONSULTANT</SelectItem>
                                            <SelectItem value="MODERATOR">MODERATOR</SelectItem>
                                            <SelectItem value="ADMIN">ADMIN</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {/* Manager Filter (SHM/FBDE) */}
                                    {!['HR','HM','AGENCY'].includes(user?.role || '') && <div className="w-full sm:w-[250px]">
                                        <SearchableSelect
                                            options={managerOptions}
                                            value={selectedManager}
                                            onValueChange={(value) => {
                                                setSelectedManager(value);
                                                setCurrentPage(1);
                                            }}
                                            onSearchChange={(value) => setManagerSearchTerm(value)}
                                            placeholder={isLoadingManagers ? "Loading managers..." : user?.role === 'ADMIN' ? 'All Managers' : 'All Recruiters'}
                                            searchPlaceholder={user?.role === 'ADMIN' ? 'Search managers...' : 'Search recruiters...'}
                                            icon={<User className="h-4 w-4 text-gray-500" />}
                                            emptyText={user?.role === 'ADMIN' ? 'No managers found' : 'No recruiters found'}
                                            className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 bg-white"
                                            loading={isLoadingManagers}
                                        />
                                    </div>}
                                    {/* Rows Per Page */}
                                    <Select
                                        value={rowsPerPage.toString()}
                                        onValueChange={(value) => {
                                            setRowsPerPage(Number(value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <SelectTrigger className="w-full sm:w-[170px] h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 bg-white">
                                            <SelectValue placeholder="Rows per page" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="10">10 rows</SelectItem>
                                            <SelectItem value="20">20 rows</SelectItem>
                                            <SelectItem value="50">50 rows</SelectItem>
                                            <SelectItem value="100">100 rows</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {/* Clear Filters Button */}
                                    {(searchTerm || roleFilter !== "all" || selectedManager !== "all") && (
                                        <Button
                                            onClick={() => {
                                                setSearchTerm("");
                                                setRoleFilter("all");
                                                setSelectedManager("all");
                                                setCurrentPage(1);
                                            }}
                                            variant="outline"
                                            className="h-10 px-4 rounded-lg border-gray-200"
                                        >
                                            <X className="h-4 w-4 mr-2" />
                                            Clear
                                        </Button>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent className="py-4">
                                {tableLoading ? (
                                    <div className="space-y-3">
                                        {Array.from({ length: 5 }).map((_, idx) => (
                                            <div
                                                key={idx}
                                                className="h-16 animate-pulse rounded-lg bg-gradient-to-r from-gray-200 to-gray-300"
                                            />
                                        ))}
                                    </div>
                                ) : tableData.length > 0 ? (
                                    <>
                                        <div className="mb-3 rounded-lg border border-orange-200 bg-orange-50/70 px-3 py-2">
                                            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4 lg:grid-cols-8">
                                                <div className="font-semibold text-gray-700">Totals</div>
                                                <div className="text-gray-700">Approches: <span className="font-semibold text-gray-900">{tableTotalCounts.Approached}</span></div>
                                                <div className="text-gray-700">Sheduled: <span className="font-semibold text-gray-900">{tableTotalCounts.Walkin}</span></div>
                                                <div className="text-gray-700">Joins: <span className="font-semibold text-gray-900">{tableTotalCounts.Joined}</span></div>
                                                <div className="text-gray-700">Selections: <span className="font-semibold text-gray-900">{tableTotalCounts.Selected}</span></div>
                                                <div className="text-gray-700">Sub Jobs: <span className="font-semibold text-gray-900">{tableTotalCounts.SubJobs}</span></div>
                                                <div className="text-gray-700">Attended: <span className="font-semibold text-gray-900">{tableTotalCounts.attended}</span></div>
                                                <div className="text-gray-700">Scheduled for Tomorrow: <span className="font-semibold text-gray-900">{tableTotalCounts.walkinsForTomorrow}</span></div>
                                            </div>
                                        </div>
                                        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow className="bg-gray-50/80">
                                                        <TableHead className="font-semibold text-gray-700">
                                                            <button type="button" className="inline-flex items-center gap-1 hover:text-gray-900" onClick={() => handleColumnSort("username")}>
                                                                User {renderSortIcon("username")}
                                                            </button>
                                                        </TableHead>
                                                        <TableHead className="font-semibold text-gray-700">
                                                            <button type="button" className="inline-flex items-center gap-1 hover:text-gray-900" onClick={() => handleColumnSort("approached")}>
                                                                Approches {renderSortIcon("approached")}
                                                            </button>
                                                        </TableHead>
                                                        <TableHead className="font-semibold text-gray-700">
                                                            <button type="button" className="inline-flex items-center gap-1 hover:text-gray-900" onClick={() => handleColumnSort("walkin")}>
                                                                Sheduled {renderSortIcon("walkin")}
                                                            </button>
                                                        </TableHead>
                                                        <TableHead className="font-semibold text-gray-700">
                                                            <button type="button" className="inline-flex items-center gap-1 hover:text-gray-900" onClick={() => handleColumnSort("joining")}>
                                                                Joins {renderSortIcon("joining")}
                                                            </button>
                                                        </TableHead>
                                                        <TableHead className="font-semibold text-gray-700">
                                                            <button type="button" className="inline-flex items-center gap-1 hover:text-gray-900" onClick={() => handleColumnSort("selected")}>
                                                                Selections {renderSortIcon("selected")}
                                                            </button>
                                                        </TableHead>
                                                        <TableHead className="font-semibold text-gray-700">
                                                            <button type="button" className="inline-flex items-center gap-1 hover:text-gray-900" onClick={() => handleColumnSort("subJobs")}>
                                                                Sub Jobs {renderSortIcon("subJobs")}
                                                            </button>
                                                        </TableHead>
                                                        <TableHead className="font-semibold text-gray-700">
                                                            <button type="button" className="inline-flex items-center gap-1 hover:text-gray-900" onClick={() => handleColumnSort("attended")}>
                                                                Attended {renderSortIcon("attended")}
                                                            </button>
                                                        </TableHead>
                                                        <TableHead className="font-semibold text-gray-700">
                                                            <button type="button" className="inline-flex items-center gap-1 hover:text-gray-900" onClick={() => handleColumnSort("walkinsForTomorrow")}>
                                                                Scheduled for Tomorrow {renderSortIcon("walkinsForTomorrow")}
                                                            </button>
                                                        </TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {tableData.map((row) => (
                                                        <TableRow key={row._id} className="transition-none">
                                                            <TableCell>{row.username}  <span className="ml-2 px-2 py-[2px] text-[10px] uppercase tracking-wide rounded-full bg-gray-200 text-gray-700">
                                                                {row.role}
                                                            </span></TableCell>
                                                            <TableCell>{row.approchCount}</TableCell>
                                                            <TableCell>{row.walkinCount}</TableCell>
                                                            <TableCell>{row.joinCount}</TableCell>
                                                            <TableCell>{row.selectionCount}</TableCell>
                                                            <TableCell>{row.subJobsCount}</TableCell>
                                                            <TableCell>{row.attended}</TableCell>
                                                            <TableCell>{row.scheduledForTomorrowCount}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>

                                        {/* Pagination Controls */}
                                        {totalPages > 1 && (
                                            <div className="mt-6 flex items-center justify-between">
                                                <div className="text-sm text-gray-600">
                                                    Showing {startIndex} to {endIndex} of {totalResults} results
                                                </div>
                                                <Pagination>
                                                    <PaginationContent>
                                                        <PaginationItem>
                                                            <PaginationPrevious
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    if (currentPage > 1) {
                                                                        handlePageChange(currentPage - 1);
                                                                    }
                                                                }}
                                                                className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                                            />
                                                        </PaginationItem>

                                                        {(() => {
                                                            const pages: (number | string)[] = [];
                                                            const showEllipsis = totalPages > 7;

                                                            if (!showEllipsis) {
                                                                for (let i = 1; i <= totalPages; i++) {
                                                                    pages.push(i);
                                                                }
                                                            } else {
                                                                pages.push(1);

                                                                if (currentPage <= 4) {
                                                                    for (let i = 2; i <= 5; i++) {
                                                                        pages.push(i);
                                                                    }
                                                                    pages.push('ellipsis-end');
                                                                    pages.push(totalPages);
                                                                } else if (currentPage >= totalPages - 3) {
                                                                    pages.push('ellipsis-start');
                                                                    for (let i = totalPages - 4; i <= totalPages; i++) {
                                                                        pages.push(i);
                                                                    }
                                                                } else {
                                                                    pages.push('ellipsis-start');
                                                                    pages.push(currentPage - 1);
                                                                    pages.push(currentPage);
                                                                    pages.push(currentPage + 1);
                                                                    pages.push('ellipsis-end');
                                                                    pages.push(totalPages);
                                                                }
                                                            }

                                                            return pages.map((page, idx) => {
                                                                if (page === 'ellipsis-start' || page === 'ellipsis-end') {
                                                                    return (
                                                                        <PaginationItem key={`ellipsis-${idx}`}>
                                                                            <span className="px-2">...</span>
                                                                        </PaginationItem>
                                                                    );
                                                                }
                                                                return (
                                                                    <PaginationItem key={page}>
                                                                        <PaginationLink
                                                                            href="#"
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                handlePageChange(page as number);
                                                                            }}
                                                                            isActive={currentPage === page}
                                                                            className="cursor-pointer"
                                                                        >
                                                                            {page}
                                                                        </PaginationLink>
                                                                    </PaginationItem>
                                                                );
                                                            });
                                                        })()}

                                                        <PaginationItem>
                                                            <PaginationNext
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    if (currentPage < totalPages) {
                                                                        handlePageChange(currentPage + 1);
                                                                    }
                                                                }}
                                                                className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                                            />
                                                        </PaginationItem>
                                                    </PaginationContent>
                                                </Pagination>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <Card className="border-2 border-dashed border-gray-300 bg-white">
                                        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                                            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-gray-100 to-gray-200 shadow-inner">
                                                <TrendingUp className="h-8 w-8 text-gray-400" />
                                            </div>
                                            <h3 className="mb-2 text-lg font-bold text-gray-900">
                                                No highlights data found
                                            </h3>
                                            <p className="max-w-sm text-sm font-medium text-gray-600">
                                                No highlights data available at the moment.
                                            </p>
                                        </CardContent>
                                    </Card>
                                )}
                            </CardContent>
                        </Card>
                    </section>)
                }
            </div>
        </div>
    );
};

export default HighlightsPage;

